/**
 * CustomTypeWriter component that displays text with a typewriter effect
 * Renders content with a natural typing animation, supporting Markdown formatting
 */
import React, { useState, useEffect, useRef } from 'react';
import { renderArticleContent } from '../../utils/html';
import { useTheme } from 'styled-components';
import { Theme } from '../../theme/lightTheme';

/**
 * StreamingWordTypeWriter component that animates text typing word by word
 * Creates a realistic typing effect by adding text chunks sequentially
 *
 * @param text - The text content to be typed
 * @param speed - The typing speed in milliseconds per chunk (lower is faster)
 * @param isRTL - Whether text should be displayed in RTL direction
 * @returns Component with animated typing effect
 */
function StreamingWordTypeWriter({
  text = '',
  speed = 30,
  isRTL = false,
}: {
  text: string;
  speed?: number;
  isRTL?: boolean;
}) {
  const theme = useTheme() as Theme;
  const [displayedText, setDisplayedText] = useState('');
  // How many chunks have been "typed" so far
  const typedCountRef = useRef(0);
  // Interval reference for typing
  const intervalRef = useRef<number | null>(null);

  /**
   * Effect that handles the typing animation
   * Updates the displayed text incrementally to create typing effect
   */
  useEffect(() => {
    /**
     * 1) Split the new text into "chunks" that include words + whitespace.
     *    For example, "Hello ```js\ncode" might become:
     *      ["Hello", " ", "```js", "\n", "code"]
     *    This preserves code fences and newlines for Markdown.
     */
    const newChunks = text.match(/[^\s]+|\s+/g) || [];

    // 2) Split the already-displayed text in the same way
    const oldChunks = displayedText.match(/[^\s]+|\s+/g) || [];

    // Join them (removing all chunk boundaries) to compare easily
    const oldJoined = oldChunks.join('');
    const newJoined = newChunks.join('');

    // Check if this is an append
    if (newJoined.startsWith(oldJoined) && newChunks.length > oldChunks.length) {
      // It's appended: continue from where we left off
      typedCountRef.current = oldChunks.length;
    } else if (newJoined === oldJoined) {
      // No actual change, do nothing
      return;
    } else {
      // The text changed in a non-append way or got shorter
      // Reset everything
      typedCountRef.current = 0;
      setDisplayedText('');
    }

    // Clear out any previous interval to avoid duplicates
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    /**
     * 3) Create an interval that adds one "chunk" at a time:
     *    e.g., a word or the whitespace/newline after it.
     */
    intervalRef.current = window.setInterval(() => {
      setDisplayedText((prev) => {
        // If we've typed all new chunks, stop
        if (typedCountRef.current >= newChunks.length) {
          if (intervalRef.current) clearInterval(intervalRef.current);
          return prev;
        }

        // Otherwise, add the next chunk
        typedCountRef.current++;
        const typedSoFar = newChunks.slice(0, typedCountRef.current).join('');
        return typedSoFar;
      });
    }, speed);

    // Cleanup on unmount or when text changes again
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [text, speed, displayedText]);

  return (
    <div style={{ direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
      {renderArticleContent(displayedText, {
        color: theme.voiceQuestionInput.text,
        isRTL: isRTL,
      })}
    </div>
  );
}

export default StreamingWordTypeWriter;
